(() => {
  const manualCountryKey = 'pw-localization-manual-country';
  const manualCountryCookie = 'pw_localization_manual_country';
  const manualChoiceParam = 'pw_country_selected';
  let manualChoiceSubmitted = false;

  function readCookie(name) {
    if (!document.cookie) return '';
    const prefix = `${name}=`;
    return document.cookie.split('; ').find((item) => item.startsWith(prefix))?.slice(prefix.length) || '';
  }

  function getManualCountryChoice(allowedCountries) {
    const candidates = [readCookie(manualCountryCookie)];
    try {
      candidates.push(window.localStorage.getItem(manualCountryKey));
    } catch (_) {}
    // Prefer a valid first-party cookie; localStorage remains a fallback.
    return candidates.find((country) => allowedCountries.includes(country)) || '';
  }

  function rememberManualCountryChoice(countryCode) {
    if (!countryCode) return;
    try {
      window.localStorage.setItem(manualCountryKey, countryCode);
    } catch (_) {}
    document.cookie = `${manualCountryCookie}=${encodeURIComponent(countryCode)}; path=/; max-age=31536000; SameSite=Lax`;
  }

  function selectableCountries() {
    return [...new Set([...document.querySelectorAll('button[name="country_code"]')].map((button) => button.value))];
  }

  function restoreManualChoiceFromUrl(allowedCountries) {
    const url = new URL(location.href);
    const countryCode = url.searchParams.get(manualChoiceParam);
    if (!countryCode) return '';
    const validCountry = allowedCountries.includes(countryCode) ? countryCode : '';
    if (validCountry) rememberManualCountryChoice(validCountry);
    url.searchParams.delete(manualChoiceParam);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    return validCountry;
  }

  function resolveTargetCountry(context, availableCountries) {
    const target = context?.detected_values?.country?.handle;
    return target && availableCountries.includes(target) ? target : '';
  }

  window.PWLocalizationContext = { resolveTargetCountry };

  async function detectVisitorCountry() {
    const endpoint = new URL(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json`, location.origin);
    endpoint.searchParams.set('country[enabled]', 'true');
    try {
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      return response.ok ? await response.json() : null;
    } catch (_) {
      return null;
    }
  }

  function previewReturnTo() {
    const url = new URL(location.href);
    const theme = window.Shopify?.theme;
    if (['unpublished', 'development'].includes(theme?.role) && /^\d+$/.test(String(theme.id))) url.searchParams.set('preview_theme_id', String(theme.id));
    return url.pathname + url.search + url.hash;
  }

  function submitCountry(targetCountry, manualCountry) {
    const returnUrl = new URL(previewReturnTo(), location.origin);
    if (manualCountry) returnUrl.searchParams.set(manualChoiceParam, manualCountry);
    const form = document.createElement('form');
    form.action = `${window.Shopify?.routes?.root || '/'}localization`;
    form.method = 'post';
    form.hidden = true;
    form.dataset.pwAutoLocalization = 'true';
    for (const [name, value] of Object.entries({
      _method: 'put',
      country_code: targetCountry,
      return_to: returnUrl.pathname + returnUrl.search + returnUrl.hash
    })) {
      const input = document.createElement('input');
      input.name = name;
      input.value = value;
      form.append(input);
    }
    document.body.append(form);
    form.submit();
  }

  function registerManualChoiceListener() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.dataset.pwAutoLocalization === 'true') return;
      const submitter = event.submitter;
      const countryCode = submitter?.getAttribute('name') === 'country_code' ? submitter.value : new FormData(form).get('country_code');
      if (!selectableCountries().includes(countryCode)) return;
      manualChoiceSubmitted = true;
      rememberManualCountryChoice(countryCode);
      const returnUrl = new URL(previewReturnTo(), location.origin);
      returnUrl.searchParams.set(manualChoiceParam, countryCode);
      let returnInput = form.querySelector('input[name="return_to"]');
      if (!returnInput) {
        returnInput = document.createElement('input');
        returnInput.name = 'return_to';
        form.append(returnInput);
      }
      returnInput.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;
    }, true);
  }

  function usesDedicatedMarketRoute() {
    const host = location.hostname.toLowerCase().replace(/^www\./, '');
    const countryDomains = [
      'pipandwells.at', 'pipandwells.be', 'pipandwells.de',
      'pipandwells.fr', 'pipandwells.it', 'pipandwells.nl',
      'pipandwells.pl', 'pipandwells.se', 'pipandwells.es',
      'pipandwells.co.uk'
    ];
    if (countryDomains.includes(host)) return true;
    const root = window.Shopify?.routes?.root || '/';
    return host === 'pipandwells.com' && root !== '/' &&
      (location.pathname === root.replace(/\/$/, '') || location.pathname.startsWith(root));
  }

  async function restoreCountryContext(script) {
    const selectable = selectableCountries();
    const availableCountries = script.dataset.availableCountries.split(',').filter(Boolean);
    const allowedCountries = availableCountries.filter((country) => selectable.includes(country));
    const urlCountry = restoreManualChoiceFromUrl(allowedCountries);

    const currentCountry = script.dataset.currentCountry;
    if (!currentCountry) return;

    // Keep the country domain or language path the visitor deliberately opened.
    // Explicit country-selector submissions still use the normal Shopify form.
    if (usesDedicatedMarketRoute()) return;

    let manualCountry = urlCountry || getManualCountryChoice(allowedCountries);
    let targetCountry = manualCountry;
    if (!targetCountry) {
      const context = await detectVisitorCountry();
      // A deliberate choice made while detection runs always wins.
      if (manualChoiceSubmitted) return;
      manualCountry = getManualCountryChoice(allowedCountries);
      targetCountry = manualCountry || resolveTargetCountry(context, allowedCountries);
    }
    if (targetCountry && targetCountry !== currentCountry) submitCountry(targetCountry, manualCountry);
  }

  if (!document.addEventListener) return;
  document.addEventListener('DOMContentLoaded', () => {
    registerManualChoiceListener();
    const script = document.currentScript || document.querySelector('[data-pw-localization-context]');
    if (script) restoreCountryContext(script);
  }, { once: true });
})();
